import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import crmOportunidadService from '../../../services/crmOportunidadService';
import type { CrmOportunidadListItem, CrmPipeline, CrmPipelineEtapa } from '../../../types/crm';
import { formatCurrency } from '../../../utils/formatters';
import { getApiErrorMessage } from '../../../utils/apiError';

type PipelineSelection = number | 'all' | '';

export interface CrmPipelineListViewProps {
  pipelines: CrmPipeline[];
  pipelineId: PipelineSelection;
  nroVendedor: number | null;
  searchTerm: string;
  onRowClick: (deal: CrmOportunidadListItem) => void;
  refreshKey?: number;
}

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'Abierta', label: 'Abierta' },
  { value: 'Ganada', label: 'Ganada' },
  { value: 'Perdida', label: 'Perdida' },
] as const;

const normEtapaNombre = (nombre?: string | null) => (nombre ?? '').trim().toLowerCase();

const estadoChipColor = (estado: string): 'default' | 'success' | 'error' | 'primary' => {
  const e = (estado || '').toLowerCase();
  if (e.includes('ganad')) return 'success';
  if (e === 'perdida') return 'error';
  if (e === 'abierta') return 'primary';
  return 'default';
};

const CrmPipelineListView: React.FC<CrmPipelineListViewProps> = ({
  pipelines,
  pipelineId,
  nroVendedor,
  searchTerm,
  onRowClick,
  refreshKey = 0,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<CrmOportunidadListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [estado, setEstado] = useState('');
  const [etapaFilter, setEtapaFilter] = useState<number | '' | string>('');

  const isAllPipelines = pipelineId === 'all';

  const etapaOptions = useMemo(() => {
    if (typeof pipelineId === 'number') {
      const pipe = pipelines.find((p) => p.id === pipelineId);
      return (pipe?.etapas ?? [])
        .slice()
        .sort((a, b) => a.orden - b.orden)
        .map((e) => ({ key: String(e.id), value: e.id as number | string, label: e.nombre }));
    }
    if (isAllPipelines) {
      const byName = new Map<string, CrmPipelineEtapa>();
      pipelines.forEach((p) => {
        (p.etapas ?? []).forEach((e) => {
          const key = normEtapaNombre(e.nombre);
          if (!key || byName.has(key)) return;
          byName.set(key, e);
        });
      });
      return Array.from(byName.values())
        .sort((a, b) => {
          const rank = (e: CrmPipelineEtapa) => (e.esCierrePerdido ? 2 : e.esCierreGanado ? 1 : 0);
          return rank(a) - rank(b) || a.orden - b.orden || a.nombre.localeCompare(b.nombre);
        })
        .map((e) => ({
          key: normEtapaNombre(e.nombre),
          value: e.nombre as number | string,
          label: e.nombre,
        }));
    }
    return [];
  }, [pipelines, pipelineId, isAllPipelines]);

  useEffect(() => {
    setEtapaFilter('');
    setPage(0);
  }, [pipelineId]);

  useEffect(() => {
    setPage(0);
  }, [nroVendedor, searchTerm, estado, etapaFilter]);

  const pipelineNombreById = useMemo(
    () => Object.fromEntries(pipelines.map((p) => [p.id, p.nombre])),
    [pipelines],
  );

  const loadList = useCallback(async () => {
    if (pipelineId === '') return;
    try {
      setLoading(true);
      const data = await crmOportunidadService.getAll({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        ...(pipelineId === 'all' ? {} : { pipelineId }),
        nroVendedor: nroVendedor ?? undefined,
        searchTerm: searchTerm.trim() || undefined,
        estado: estado || undefined,
        ...(typeof etapaFilter === 'number'
          ? { etapaId: etapaFilter }
          : typeof etapaFilter === 'string' && etapaFilter
            ? { etapaNombre: etapaFilter }
            : {}),
      });
      setItems(data.items ?? []);
      setTotalCount(data.totalCount ?? 0);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar la lista'), { variant: 'error' });
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    pipelineId,
    page,
    rowsPerPage,
    nroVendedor,
    searchTerm,
    estado,
    etapaFilter,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    const t = setTimeout(() => {
      void loadList();
    }, 250);
    return () => clearTimeout(t);
  }, [loadList, refreshKey]);

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField
          select
          size="small"
          label="Etapa"
          value={etapaFilter}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') {
              setEtapaFilter('');
              return;
            }
            setEtapaFilter(isAllPipelines ? v : Number(v));
          }}
          sx={{ width: { xs: '100%', md: 220 }, minWidth: { md: 220 } }}
        >
          <MenuItem value="">Todas</MenuItem>
          {etapaOptions.map((opt) => (
            <MenuItem key={opt.key} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          sx={{ width: { xs: '100%', md: 180 }, minWidth: { md: 180 } }}
        >
          {ESTADOS.map((opt) => (
            <MenuItem key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Cargando lista...
        </Typography>
      )}

      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Oportunidad</TableCell>
              {isAllPipelines && <TableCell>Pipeline</TableCell>}
              <TableCell>Etapa</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Monto</TableCell>
              <TableCell sx={{ minWidth: 110 }}>Probabilidad</TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell align="right">Días</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((deal) => {
              const prob =
                deal.probabilidad != null
                  ? Math.max(0, Math.min(100, Number(deal.probabilidad)))
                  : null;
              return (
                <TableRow
                  key={deal.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onRowClick(deal)}
                >
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {deal.nombre}
                      </Typography>
                      {deal.estancada && (
                        <Chip size="small" label="Estancada" color="warning" sx={{ height: 20, fontSize: 10, alignSelf: 'flex-start' }} />
                      )}
                    </Stack>
                  </TableCell>
                  {isAllPipelines && (
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {pipelineNombreById[deal.pipelineId] || '—'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {deal.etapaColor && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: deal.etapaColor,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography variant="body2">{deal.etapaNombre || '—'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={deal.estado}
                      color={estadoChipColor(deal.estado)}
                      variant={deal.estado === 'Abierta' ? 'outlined' : 'filled'}
                      sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {formatCurrency(deal.monto)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {prob != null ? (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {prob}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={prob}
                          sx={{
                            height: 4,
                            borderRadius: 1,
                            mt: 0.25,
                            bgcolor: 'rgba(0,0,0,0.06)',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: prob >= 70 ? '#2e7d32' : prob >= 40 ? '#ed6c02' : '#d32f2f',
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {deal.nombreVendedor || `Vend. ${deal.nroVendedor}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {deal.contactoPrincipal || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {deal.diasEnEtapa}d
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAllPipelines ? 9 : 8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No hay oportunidades para los filtros seleccionados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Filas"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
        }
      />
    </Stack>
  );
};

export default CrmPipelineListView;
